import React from "react";
import {
  Text,
  VStack,
  Container,
  Heading,
  Box,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";

const APP_NAME = "AI Interior Design Decoration";
const CONTACT_EMAIL = "mdeniz.info@gmail.com";
const EFFECTIVE_DATE = "October 20, 2026";

// Route used by the app (src/config/legal.ts): /aiinteriordesign/termsofuse
function TermsOfUse() {
  const appName = APP_NAME;

  const Section = ({ title, children }) => (
    <Box mb={8}>
      <Text fontSize="m" color="#fff" fontWeight="bold" mb={2}>
        {title}
      </Text>
      <Text fontSize="m" color="#fff">
        {children}
      </Text>
    </Box>
  );

  const SubSection = ({ title, children }) => (
    <Box ml={4} mb={4}>
      <Text fontSize="m" color="#fff" fontWeight="semibold" mb={2}>
        {title}
      </Text>
      <Text fontSize="m" color="#fff">
        {children}
      </Text>
    </Box>
  );

  return (
    <div className="container">
      <div className="header">
        <div className="header__right">
          <ChakraLink href="/">
            <Text fontWeight="black" color="#fff" fontSize={20}>
              Muhammed Deniz
            </Text>
          </ChakraLink>
        </div>

        <div className="header__left">
          <Link to="/home">
            <Text fontSize="m" mr={5} fontWeight="black" color="#fff">
              Home
            </Text>
          </Link>
          <Link to="/aiinteriordesign/privacypolicy">
            <Text fontSize="m" mr={5} fontWeight="black" color="#fff">
              Privacy Policy
            </Text>
          </Link>
          <Link to="/aiinteriordesign/termsofuse">
            <Text fontSize="m" mr={5} fontWeight="black" color="#fff">
              Terms Of Use
            </Text>
          </Link>
        </div>
      </div>

      <Container maxW="container.lg" py={16}>
        <VStack align="stretch" spacing={6}>
          <Heading as="h1" size="xl" color="#fff" mb={8}>
            Terms of Use
          </Heading>

          <Text fontSize="m" color="#fff">
            Welcome to <strong>{appName}</strong>! These terms and conditions
            outline the rules and regulations for the use of our apps, including{" "}
            <strong>{appName}</strong>.
          </Text>

          <Text fontSize="m" color="#fff">
            By accessing and using <strong>{appName}</strong>, you agree to be
            bound by these terms and conditions. If you do not agree to these
            terms, please do not use the application.
          </Text>

          <Section title="1. User License">
            Permission is granted to download, install, and use{" "}
            <strong>{appName}</strong> for personal and non-commercial purposes
            on compatible devices, solely for creating interior design and
            decoration ideas from photos of rooms and spaces as described in the
            app.
          </Section>

          <Section title="2. User Obligations">
            You agree not to use <strong>{appName}</strong> for any illegal or
            unauthorized purpose, and you must not violate any laws in your
            jurisdiction. You also agree not to modify, adapt, or hack{" "}
            <strong>{appName}</strong> or modify another website to falsely
            imply that it is associated with <strong>{appName}</strong>. You may
            not decompile or reverse engineer the app, remove any copyright or
            proprietary notices, or copy or transfer the app to another person.
          </Section>

          <Section title="3. Your Photos and Results">
            The photos and text you submit and the results created from them are
            your responsibility. You keep ownership of your photos. You grant us
            a limited, non-exclusive license to process your photo and text,
            including by sending them to our AI service providers described in
            the Privacy Policy, only to create the result you requested and to
            check it for safety. You may use the results for your personal,
            non-commercial purposes, subject to these terms and to the rights of
            any person or property owner shown in the photo. Because results are
            generated by AI, similar or identical results may be created for
            other users, and we cannot guarantee that a result is unique or free
            from third-party rights.
          </Section>

          <Section title="4. Subscription Services">
            <SubSection title="4.1 Subscription Terms">
              • <strong>{appName}</strong> offers auto-renewable subscription
              options:
              <br />
              • Weekly Subscription
              <br />
              • Yearly Subscription
              <br />
              • Creating results requires an active subscription
              <br />
              • Subscription automatically renews unless auto-renew is turned
              off at least 24-hours before the end of the current period
              <br />
              • Account will be charged for renewal within 24-hours prior to the
              end of the current period
              <br />• Subscriptions may be managed by the user and auto-renewal
              may be turned off by going to the user's Account Settings after
              purchase
            </SubSection>

            <SubSection title="4.2 Payment and Billing">
              • Payment will be charged to your Apple ID account at confirmation
              of purchase
              <br />
              • All purchases are processed through the Apple App Store using
              your Apple ID
              <br />
              • On Android, payment will be charged to your Google Play account
              at confirmation of purchase and purchases are processed through
              Google Play
              <br />
              • Prices are subject to change without notice
              <br />• Subscription prices are clearly displayed in the app
              before purchase
            </SubSection>

            <SubSection title="4.3 Cancellation and Refunds">
              • You can cancel your subscription at any time through your Apple
              ID account settings (or, on Android, in Google Play under Payments
              &amp; subscriptions)
              <br />
              • Cancellation will take effect at the end of the current
              subscription period
              <br />
              • No refunds will be issued for partial subscription periods,
              except where required by law
              <br />• Free trial periods, if offered, will automatically convert
              to paid subscriptions unless cancelled before the trial period
              ends
            </SubSection>
          </Section>

          <Section title="5. Intellectual Property">
            <strong>{appName}</strong> and its original content, features, and
            functionality are owned by us and are protected by international
            copyright, trademark, patent, trade secret, and other intellectual
            property or proprietary rights laws.
          </Section>

          <Section title="6. App Store Requirements">
            • All purchases and subscriptions are processed through Apple's App
            Store (or Google Play on Android)
            <br />
            • Use of the app is also subject to Apple's Terms of Service (or
            Google Play's Terms of Service on Android)
            <br />• Any issues related to billing or purchases should be
            directed to Apple Support (or Google Play Support on Android)
          </Section>

          <Section title="7. Limitations">
            We shall not be liable for any damages arising from your use of{" "}
            <strong>{appName}</strong>, including but not limited to direct,
            indirect, incidental, punitive, and consequential damages, to the
            fullest extent permitted by law. This includes any purchase,
            renovation, construction or decoration decision you make based on a
            result.
          </Section>

          <Section title="8. Changes">
            We reserve the right to modify or replace these terms at any time.
            By continuing to access or use <strong>{appName}</strong> after such
            changes, you agree to be bound by the updated terms.
          </Section>

          <Section title="9. Governing Law">
            These terms are governed by and construed in accordance with the
            laws of the Republic of Türkiye, and you irrevocably submit to the
            exclusive jurisdiction of the courts of İstanbul, without regard to
            conflict of law provisions. Mandatory consumer protection rights
            that apply where you live are not affected.
          </Section>

          <Section title="10. AI Features and Third-Party AI Services">
            <strong>{appName}</strong> uses artificial intelligence to redesign
            the room or space shown in the photo you choose, using the style and
            optional description you provide. Your photo and description are
            sent to third-party AI service providers to generate the result and
            to check it for safety, only after you give your permission in the
            app. You can withdraw this permission at any time in the app's
            settings. Results are generated by AI, may be inaccurate or
            unexpected, and may change or invent details such as the layout,
            proportions, furniture or lighting of a space. Please see our
            Privacy Policy for details on how your data is handled.
          </Section>

          <Section title="11. Design Results Are for Inspiration Only">
            Results are illustrative images intended for inspiration. They are
            not architectural, engineering, structural, electrical, safety or
            professional design advice, and they are not a quote, plan or
            guarantee. Furniture, products, materials, colors, sizes and
            finishes shown may not exist, may not be available, and may not look
            the same in real life. Always consult a qualified professional
            before making construction, renovation or purchase decisions.
          </Section>

          <Section title="12. Acceptable Use of Photos and AI-Generated Content">
            You may only upload photos that you own or that you have the right
            to use, and photos of people only if they have given you clear
            permission. You are responsible for the photos and text you submit.
            You agree not to use <strong>{appName}</strong> to create, request,
            or share:
            <br />
            • nudity, sexual or sexually suggestive content
            <br />
            • any content that involves or depicts a child or minor
            <br />
            • content made from another person's photo or private space without
            their permission, or content intended to harass, deceive,
            impersonate, defame or violate someone's privacy
            <br />
            • violent, graphic, hateful, extremist, or illegal content
            <br />• attempts to bypass or disable our safety filters or rules
            (including text written to override the app's instructions)
          </Section>

          <Section title="13. Moderation, Reporting and Enforcement">
            We check photos, text and results for unsafe content and may use the
            safety tools of our AI providers. You can report any result inside
            the app using the Report button, or contact us at the email address
            below. We review reports and may remove content, restrict features,
            or block access for users who violate these terms, with or without
            notice. Where required by law, we may report illegal content to the
            relevant authorities.
          </Section>

          <Section title="14. Termination">
            You may stop using <strong>{appName}</strong> at any time by
            deleting the app. We may suspend or end your access to the app or to
            some of its features if you violate these terms or misuse the
            service. Ending your access does not cancel an active subscription;
            you must cancel it through your Apple ID or Google Play account.
          </Section>

          <Section title="15. Disclaimer of Warranties">
            <strong>{appName}</strong> and all results are provided "as is" and
            "as available" without warranties of any kind, either express or
            implied, including accuracy, availability, or fitness for a
            particular purpose, to the fullest extent permitted by law.
          </Section>

          <Section title="16. Privacy and Device Permissions">
            Your use of <strong>{appName}</strong> is also governed by our
            Privacy Policy, which explains what data is collected, how it is
            used, and how to withdraw your consent or request deletion. The app
            may ask for optional permissions on your device, such as access to
            your photos (to choose a photo and to save results) and the tracking
            (advertising identifier) permission. You can decline or change these
            permissions at any time in your device settings, and the app's
            features do not depend on your agreeing to tracking.
          </Section>

          <Section title="17. Apple App Store Terms">
            If you downloaded <strong>{appName}</strong> from the Apple App
            Store, the following also applies:
            <br />
            • Acknowledgement: these terms are between you and Muhammed Deniz
            only, not Apple. We, not Apple, are solely responsible for the app
            and its content.
            <br />
            • Scope of license: you are granted a limited, non-transferable
            license to use the app on Apple-branded products that you own or
            control, as permitted by the Usage Rules in the App Store Terms of
            Service.
            <br />
            • Maintenance and support: we are solely responsible for providing
            maintenance and support. Apple has no obligation to provide any
            maintenance or support for the app.
            <br />
            • Warranty: if the app fails to conform to any applicable warranty,
            you may notify Apple, and Apple will refund the purchase price of
            the app to you. To the maximum extent permitted by law, Apple has no
            other warranty obligation with respect to the app.
            <br />
            • Product claims: we, not Apple, are responsible for addressing any
            claims relating to the app or your possession or use of it,
            including product liability claims, claims that the app fails to
            conform to legal or regulatory requirements, and consumer protection
            or privacy claims.
            <br />
            • Intellectual property: if a third party claims that the app
            infringes its intellectual property rights, we, not Apple, are
            solely responsible for the investigation, defense, settlement and
            discharge of that claim.
            <br />
            • Legal compliance: you represent that you are not located in a
            country subject to a U.S. Government embargo or designated as a
            "terrorist supporting" country, and that you are not on any U.S.
            Government list of prohibited or restricted parties.
            <br />• Developer contact: questions, complaints or claims about the
            app can be sent to Muhammed Deniz, {CONTACT_EMAIL}.
            <br />
            • Third-party terms: you must comply with any applicable third-party
            terms when using the app, such as your wireless data service
            agreement.
            <br />• Third-party beneficiary: Apple and its subsidiaries are
            third-party beneficiaries of these terms, and, once you accept them,
            Apple may enforce them against you as a third-party beneficiary.
          </Section>

          <Box mt={10}>
            <Text fontSize="m" color="#fff">
              If you have any questions about these terms, please contact us at{" "}
              {CONTACT_EMAIL}.
            </Text>
          </Box>

          <Box mt={10}>
            <Text fontSize="s" color="#fff" textAlign="right">
              Last updated: {EFFECTIVE_DATE}
            </Text>
            <Text
              fontSize="s"
              color="#fff"
              fontWeight="hairline"
              textAlign="right"
            >
              <strong>{appName}</strong> by Muhammed Deniz
            </Text>
            <Text fontSize="s" color="#fff" textAlign="right" mt={2}>
              Contact:{" "}
              <ChakraLink href={`mailto:${CONTACT_EMAIL}`} color="#fff">
                {CONTACT_EMAIL}
              </ChakraLink>
            </Text>
          </Box>
        </VStack>
      </Container>
    </div>
  );
}

export default TermsOfUse;
